import { Anchor, Box, Divider, Group, Select } from '@byecode/ui'
import type { Field, IndependentAction, TypeInstanceMap } from '@lighthouse/core'
import type { FlowNode, NodeTypes, VariableTree2, WechatTemplateMsgActionConfig } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    PAGE_LINK_OPTION,
    useAtomData,
    workflowGuidanceDocumentMap
} from '@lighthouse/shared'
import { equals, find, uniq } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { useGetWxMessageInfo } from '@/shared/reusable'

import { ActionAuthIntegrateCard } from '../../Common/ActionAuthIntegrateCard'
import { GroupRequired } from '../../Common/GroupRequired'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { MessageTargetPerson } from '../component/MessageTargetPerson'
import { ContentSetting } from './ContentSetting'
import * as SC from './styles'

const templateDocument = 'https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/sh28sm0z7ypsyps7#Naumw'
interface WxTemplateActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    value: WechatTemplateMsgActionConfig
    onChange: (value: WechatTemplateMsgActionConfig) => void
}

export const WxTemplateActionConfigure: React.FC<WxTemplateActionConfigureProps> = ({
    allParentNodes,
    allParentActions,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const { viewDsId = '', formDsId = '', viewType } = useActionAdderDepParams()
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { type } = useFlowContainerContext()

    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const { data } = useGetWxMessageInfo(appId)

    const formDataSource = useMemo(() => dataSourceList.find(ds => ds.id === formDsId), [dataSourceList, formDsId])
    const viewDataSource = useMemo(() => dataSourceList.find(ds => ds.id === viewDsId), [dataSourceList, viewDsId])

    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const { customViewOption } = useVariableCustomViewOption()
    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({ dataSource: viewDataSource, viewType })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }

        if (customViewOption) {
            options.push(customViewOption)
        }

        return options
    }, [customViewOption, viewDataSource, viewType, isInFlow])

    const formOption = useMemo(
        () =>
            formDataSource && !isInFlow
                ? getFormDatasourceOptions({
                      dataSource: formDataSource
                  })
                : undefined,
        [formDataSource, isInFlow]
    )

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: (allParentNodes && getAllCanBeUpstreamNodes(allParentNodes)) || [],
                allUpstreamNodes: allParentNodes || [],
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const textUserOption = useGetUserRecordOptions()
    const templateUserOption = useGetUserRecordOptions()

    const wechatMessageTemplatesOptions = useMemo(() => {
        return (
            data?.map(item => ({
                value: item.templateId ?? '',
                label: item.title ?? '',
                content: item.content ?? ''
            })) ?? []
        )
    }, [data])

    const handleTemplateChange = useCallback(
        (templateId: string) => {
            const currentOption = find(option => option.value === templateId, wechatMessageTemplatesOptions)?.content ?? ''
            const variablesMatches = currentOption.match(/{{([\w.]+)}}/g) ?? []
            const variables = variablesMatches.map(item => {
                return item.replace(/{{(\w+)\.\w+}}/g, '$1')
            })
            const uniqVariables = uniq(variables)

            const currentValues = value.values ?? []
            const valuesVariableNames = currentValues.map(v => v.variableName)

            const next = { ...value, templateId }

            if (equals(valuesVariableNames, uniqVariables)) {
                onChange(next)
                return
            }
            const newValues = uniqVariables.map(field => {
                const v = find(item => item.variableName === field, currentValues)?.value ?? {}
                return { variableName: field, value: v }
            })
            onChange({ ...next, values: newValues })
        },
        [onChange, value, wechatMessageTemplatesOptions]
    )

    const wechatAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'WECHAT_OFFICIAL_ACCOUNT'), [otherPlatformList])

    const header = useMemo(() => {
        if (!actionTypeSwitcher) {
            return null
        }
        return (
            <>
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
                <Divider />
            </>
        )
    }, [actionTypeSwitcher])

    if (!wechatAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="WECHAT_OFFICIAL_ACCOUNT" />
            </>
        )
    }

    return (
        <CM.Container>
            {header}
            <ActionAuthIntegrateCard type="WECHAT_OFFICIAL_ACCOUNT" />
            {!actionTypeSwitcher && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="动作配置">
                        <ListItem4ByecodeUi>
                            <CM.FormItemLabelWrapper>
                                <CM.FormItemLabel>类型</CM.FormItemLabel>
                            </CM.FormItemLabelWrapper>
                            <CM.FormItemContent>
                                {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                            </CM.FormItemContent>
                        </ListItem4ByecodeUi>
                    </Group>
                </>
            )}

            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="发送给" required>
                <Box px={8}>
                    <SC.DescriptionWrapper>
                        <SC.Description>
                            请选择{' '}
                            <Link to={`/${appId}/dataSource/USER_DATASOURCE`} target="_blank">
                                用户表
                            </Link>{' '}
                            中包含“微信网页授权_OpenID”的人员或人员变量，除此之外人员要在微信关注公众号才能接收到消息。
                        </SC.Description>
                    </SC.DescriptionWrapper>
                    <MessageTargetPerson
                        workflowList={workflowList}
                        dataSourceList={dataSourceList}
                        parentNodes={allParentNodes}
                        value={value.personList}
                        onChange={personList => onChange({ ...value, personList })}
                    />
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="消息模版" required>
                <Box px={8}>
                    <SC.SelectContainer>
                        <Select
                            placeholder="请选择"
                            options={wechatMessageTemplatesOptions}
                            value={value.templateId}
                            onChange={templateId => handleTemplateChange(templateId)}
                        />
                    </SC.SelectContainer>
                    <SC.Description marginTop>
                        模板消息需在公众号中配置{' '}
                        <Anchor to={workflowGuidanceDocumentMap.WECHAT_TEMPLATE_MSG_ACTION.url} target="_blank">
                            详见
                        </Anchor>
                    </SC.Description>
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="模板参数" required>
                <Box px={8}>
                    <SC.Description>
                        微信针对不同类型的模板参数有相应要求，如果不符合会造成消息发送失败，具体要求{' '}
                        <Anchor to={templateDocument} target="_blank">
                            详见
                        </Anchor>
                    </SC.Description>
                    <ContentSetting
                        value={value.values}
                        onChange={values => onChange({ ...value, values })}
                        viewOptions={viewOptions}
                        formOption={formOption}
                        pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                        upstreamOptions={upstreamOptions}
                        userOption={templateUserOption}
                        dataSourceList={dataSourceList}
                    />
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="详情链接" required>
                <Box px={8}>
                    <NodeFieldPreview
                        innerTypes={getInputInnerTypeMatch('TEXT')}
                        placeholder="请输入"
                        value={value.url}
                        onBlur={url => onChange({ ...value, url })}
                        autoHeight
                        viewOptions={viewOptions}
                        formOption={formOption}
                        pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                        upstreamOptions={upstreamOptions}
                        userOption={textUserOption}
                        dataSourceList={dataSourceList}
                    />
                    {/* <SC.Description>模板消息需在公众号中配置 详见</SC.Description> */}
                </Box>
            </GroupRequired>
        </CM.Container>
    )
}
